import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Star, Bus, X, Loader2 } from 'lucide-react';
import { routeApi, scheduleApi, routeCityApi } from '../services/api';
import { getSriLankaDateString } from '../utils/dateUtils';
import Swal from 'sweetalert2';

const Routes = () => {
  const [allRoutes, setAllRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Timetable modal states
  const [isTimetableModalOpen, setIsTimetableModalOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [timetableData, setTimetableData] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);


  // Format time helper function
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // Handle different time formats
      if (typeof timeString === 'string') {
        // If it's already in HH:MM format
        if (timeString.match(/^\d{2}:\d{2}$/)) {
          const [hours, minutes] = timeString.split(':');
          const hour12 = parseInt(hours) % 12 || 12;
          const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
          return `${hour12}:${minutes} ${ampm}`;
        }
        // If it's a datetime string
        const date = new Date(timeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          });
        }
      }
      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  // Format duration helper function - converts minutes to hours and minutes
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '0 min';
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${remainingMinutes} min`;
  };

  // Load all active routes from database
  useEffect(() => {
    const loadRoutes = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await routeApi.getRoutes(true); // Get only active routes
        
        let routes = [];
        if (response?.value && Array.isArray(response.value)) {
          routes = response.value;
        } else if (Array.isArray(response)) {
          routes = response;
        } else if (response && typeof response === 'object') {
          routes = [response];
        }
        
        // Fetch schedules to get departure and arrival times
        const schedulesResponse = await scheduleApi.getSchedules();
        let allSchedules = [];
        if (schedulesResponse?.value && Array.isArray(schedulesResponse.value)) {
          allSchedules = schedulesResponse.value;
        } else if (Array.isArray(schedulesResponse)) {
          allSchedules = schedulesResponse;
        }
        
        // Transform routes to match the expected format with schedule times
        const transformedRoutes = await Promise.all(
          routes.map(async (route) => {
            // Calculate total price: basePrice + serviceCharge
            const basePrice = route.basePrice || 0;
            const serviceCharge = route.serviceCharge || 0;
            const totalPrice = basePrice + serviceCharge;
            
            const routeId = route.routeID || route.id;
            
            // Find first schedule for this route
            const routeSchedule = allSchedules.find(s => 
              (s.routeID || s.routeId) === routeId && (s.isActive !== false)
            );
            
            let departure = 'N/A';
            let arrival = 'N/A';
            
            // If schedule found, try to get times from schedule or schedule dates
            if (routeSchedule) {
              const scheduleId = routeSchedule.scheduleID || routeSchedule.scheduleId || routeSchedule.id;
              
              // Try to get times from schedule first
              if (routeSchedule.departureTime) {
                departure = formatTime(routeSchedule.departureTime);
              }
              if (routeSchedule.arrivalTime) {
                arrival = formatTime(routeSchedule.arrivalTime);
              }
              
              // If times not in schedule, try to get from schedule dates
              if (departure === 'N/A' || arrival === 'N/A') {
                try {
                  const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(scheduleId);
                  let scheduleDates = [];
                  
                  if (scheduleDatesResponse?.value && Array.isArray(scheduleDatesResponse.value)) {
                    scheduleDates = scheduleDatesResponse.value;
                  } else if (Array.isArray(scheduleDatesResponse)) {
                    scheduleDates = scheduleDatesResponse;
                  }
                  
                  // Get first available schedule date
                  if (scheduleDates.length > 0) {
                    const firstDate = scheduleDates[0];
                    if (firstDate.departureTime && departure === 'N/A') {
                      departure = formatTime(firstDate.departureTime);
                    }
                    if (firstDate.arrivalTime && arrival === 'N/A') {
                      arrival = formatTime(firstDate.arrivalTime);
                    }
                  }
                } catch (error) {
                  console.warn(`Error fetching schedule dates for route ${routeId}:`, error);
                }
              }
            }
            
            return {
              id: route.routeID,
              routeId: route.routeID || route.id,
              fromCityId: route.fromCityID || route.fromCityId,
              toCityId: route.toCityID || route.toCityId,
              from: route.fromLocation,
              to: route.toLocation,
              price: `Rs ${totalPrice.toFixed(2)}`,
              duration: formatDuration(route.estimatedDuration || 0),
              departure: departure,
              arrival: arrival,
              busType: 'Super Luxury',
              seatLayout: '2 x 2',
              available: route.isActive,
              rating: 4.5 + Math.random() * 0.5
            };
          })
        );
        
        setAllRoutes(transformedRoutes);
      } catch (error) {
        console.error('Error loading routes:', error);
        setError('Failed to load routes. Please try again later.');
        setAllRoutes([]);
      } finally {
        setLoading(false);
      }
    };

    loadRoutes();
  }, []);

  // Load timetable data when modal opens
  useEffect(() => {
    const loadTimetable = async () => {
      if (!isTimetableModalOpen || !selectedRouteId) {
        setTimetableData([]);
        return;
      }

      try {
        setTimetableLoading(true);
        const response = await routeCityApi.getRouteCitiesByRoute(selectedRouteId);
        const cities = response || [];
        // Sort by order
        const sortedCities = cities.sort((a, b) => (a.order || a.Order || a.orderID || 0) - (b.order || b.Order || b.orderID || 0));
        setTimetableData(sortedCities);
      } catch (error) {
        console.error('Error loading timetable:', error);
        setTimetableData([]);
        Swal.fire('Error', 'Failed to load timetable', 'error');
      } finally {
        setTimetableLoading(false);
      }
    };

    loadTimetable();
  }, [isTimetableModalOpen, selectedRouteId]);

  return (
    <div className="w-full min-h-screen">
      {/* Header Section - Very Compact */}
      <section className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">
              Bus Routes
            </h1>
            <p className="text-xs sm:text-sm text-blue-100">
              Discover all available routes across Sri Lanka
            </p>
          </div>
        </div>
      </section>

      {/* Routes Section */}
      <section className="w-full py-8 lg:py-12">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2">
                    <h3 className="text-xs font-medium text-red-800">Error</h3>
                    <p className="text-xs text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-4 border border-gray-100 animate-pulse">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-16 h-3 bg-gray-300 rounded"></div>
                      <div className="w-12 h-3 bg-gray-300 rounded"></div>
                    </div>
                    <div className="w-full h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="w-full h-3 bg-gray-300 rounded mb-3"></div>
                    <div className="w-full h-8 bg-gray-300 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              /* Routes Grid - Compact Card Layout */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {allRoutes.map((route) => (
                  <div key={route.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <span className="text-xs text-gray-500">Route</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 text-yellow-400 fill-current" />
                        <span className="text-xs text-gray-600">{route.rating.toFixed(1)}</span>
                      </div>
                    </div>

                    <div className="mb-2 flex flex-col">
                      <h3 className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2 mb-1">
                        {route.from} to {route.to}
                      </h3>
                      <p className="text-xs text-gray-500 font-medium">
                        Nuwantha Enterprises (Pvt) Ltd
                      </p>
                    </div>

                    <div className="mb-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3 text-blue-600" />
                          <span className="text-xs text-gray-500">Departure</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">{route.departure || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3 text-green-600" />
                          <span className="text-xs text-gray-500">Arrival</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">{route.arrival || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-600">{route.duration}</span>
                      </div>
                      <span className="text-base font-bold text-blue-600">{route.price}</span>
                    </div>

                    <div className="mt-auto space-y-2">
                      <button
                        onClick={() => {
                          setSelectedRouteId(route.routeId);
                          setIsTimetableModalOpen(true);
                        }}
                        className="bg-green-600 text-white py-1 px-2 rounded-lg text-xs hover:bg-green-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                        title="View Timetable"
                      >
                        Timetable
                      </button>
                      <Link
                        to={{
                          pathname: "/book",
                          search: `?routeId=${route.routeId}&date=${getSriLankaDateString()}`,
                          state: {
                            fromCityId: route.fromCityId,
                            toCityId: route.toCityId,
                            routeId: route.routeId,
                            travelDate: getSriLankaDateString(),
                            from: route.from,
                            to: route.to
                          }
                        }}
                        className="w-full bg-blue-600 text-white py-2 px-3 rounded-lg text-center block hover:bg-blue-700 transition-all duration-200 font-medium text-xs shadow-md hover:shadow-lg transform hover:scale-105"
                      >
                        Book Ticket
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Routes Message */}
            {!loading && allRoutes.length === 0 && !error && (
              <div className="text-center py-8">
                <Bus className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                <h3 className="text-base font-medium text-gray-900 mb-1">No routes available</h3>
                <p className="text-sm text-gray-500">There are currently no active routes matching your filters.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Timetable Modal */}
      {isTimetableModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsTimetableModalOpen(false)}
            />
            
            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Route Timetable
                      </h3>
                      <p className="text-blue-100 text-sm">
                        City stops and arrival times
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsTimetableModalOpen(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 max-h-96 overflow-y-auto">
                {timetableLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading timetable...</span>
                  </div>
                ) : timetableData.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">No timetable data available for this route.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {timetableData.map((city, index) => (
                      <div 
                        key={city.routeCityID || index}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-900">
                            {city.cityName || city.City?.cityName || 'Unknown City'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-5 h-5 text-gray-400" />
                          <span className="font-semibold text-gray-700">
                            {formatTime(city.arrivalTime || city.ArrivalTime)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => setIsTimetableModalOpen(false)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Routes;
